import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import classNames from 'classnames';
import React, {useMemo, useState} from 'react';

import {ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FormDefinitionPageHeader from 'forms/form-definition/FormDefinitionPageHeader';
import {FormDefinitionContent, FormActionConfigInput} from 'forms/form-designer/Constants';
import FormDesigner from 'forms/form-designer/FormDesigner';
import {
  areDesignerStatesEqual,
  FormDesignerState,
  FormDesignerStateActions,
} from 'forms/form-designer/FormDesignerState';
import FormFixBanner, {FormFixUpdate} from 'forms/form-designer/FormFixBanner';
import {InitializeFormDesignerWithFormActionQuery} from 'forms/form-designer/graphql/__generated/InitializeFormDesignerWithFormActionQuery';
import FormInstancePreview from 'forms/form-instance/FormInstancePreview';
import {getPreviousURL} from 'lib/navigateToURL';
import {GRAY_LIGHT, GRAY_LIGHTER} from 'ui-library/Colors';
import useResizeObserver from 'ui-library/hooks/useResizeObserver';
import useWarnOnNavigate from 'ui-library/hooks/useWarnOnNavigate';
import {Tab, TabContainer} from 'ui-library/TabContainer';
import TabLayout, {TabLayoutBackgroundColor} from 'ui-library/TabLayout';
import APIUtil from 'util/APIUtil';

type InitialProcessTaskSchema =
  InitializeFormDesignerWithFormActionQuery['processTaskSchemasSupportingPlatformFeature'][number];

const FORM_DEFINITION_URL_REGEX = /forms\/create/;
const TASK_SCHEMA_URL_REGEX = new RegExp(
  `/workflow-task-schemas/${APIUtil.typeToApiPrefix(ModelTypeConstants.PROCESS_TASK_SCHEMA)}`
);
// Matches a URL like "<username>/template-collections/abcdef"
const TEMPLATE_COLLECTION_URL_REGEX = /\/template-collections\/\w/;

export function getFormDefinitionActionOrigin(): ActionOrigin {
  const previousUrl = getPreviousURL();
  if (FORM_DEFINITION_URL_REGEX.test(previousUrl)) {
    return ActionOrigin.FORMS_CREATE_FORM_DEFINITION_DESIGNER;
  } else if (TASK_SCHEMA_URL_REGEX.test(previousUrl)) {
    return ActionOrigin.SETTINGS_TASK_SCHEMA_PAGE;
  } else if (TEMPLATE_COLLECTION_URL_REGEX.test(previousUrl)) {
    return ActionOrigin.NOTEBOOK_TEMPLATE_COLLECTION_PAGE;
  }
  return ActionOrigin.UI_UNDETERMINED;
}

export enum Tabs {
  BUILDER = 'BUILDER',
  PREVIEW = 'PREVIEW',
}

// Use two column layout if container width is larger than this, otherwise tab layout.
const MIN_WIDTH_TWO_COLUMN_LAYOUT = 1000;

/**
 * Responsive layout for rendering a designer and preview side by side or in tabs.
 *
 * @param designer FormDesigner or loading element
 * @param preview FormInstancePreview or loading element
 * @returns Either a two column layout (wide screen) or a tabbed layout
 */
function ResponsiveLayout({designer, preview}: {designer: JSX.Element; preview: JSX.Element}): JSX.Element {
  const {ref, width} = useResizeObserver();
  const useTabLayout = width && width < MIN_WIDTH_TWO_COLUMN_LAYOUT;
  const [activeTab, setActiveTab] = useState<Tabs>(Tabs.BUILDER);

  return (
    <div className="u-width-full u-height-full" ref={ref}>
      {useTabLayout ? (
        <TabContainer<Tabs> activeTab={activeTab} onRequestTabChange={setActiveTab} className="u-padding">
          <Tab tabKey={Tabs.BUILDER} title="Builder">
            <div className={css(styles.designerTabContainer)}>{designer}</div>
          </Tab>
          <Tab tabKey={Tabs.PREVIEW} title="Preview">
            <div className={css(styles.previewTabContainer)}>{preview}</div>
          </Tab>
        </TabContainer>
      ) : (
        <div className={css(styles.twoColumnContainer)}>
          <div className={classNames('u-padding u-flex-grow', css(styles.designerColumnContainer))}>
            {designer}
          </div>
          <div className={classNames('u-padding u-flex-grow', css(styles.previewColumnContainer))}>
            {preview}
          </div>
        </div>
      )}
    </div>
  );
}

export interface SharedFormDefinitionPageProps {
  id?: string;

  name: string;
  setName: (name: string) => void;

  templateCollectionId: string;
  setTemplateCollectionId: (name: string) => void;

  archivePurpose?: string;
  onArchive?: () => void;
  onUnarchive?: () => void;

  saveButtonText: string;
  /**
   * Callback should return whether the save was successful.
   */
  onSave: (
    formDefinition: FormDefinitionContent,
    formActionConfigs: Array<FormActionConfigInput>,
    userChanges: boolean
  ) => Promise<boolean>;

  initialFormDesignerState: FormDesignerState;
  actionToSchemas: {
    [key: string]: Array<InitialProcessTaskSchema>;
  };

  autofixUpdates?: Array<FormFixUpdate>;
  autofixTaskSchemaName?: string;
  autofixTaskSchemaURL?: string;

  // Data for analytics payload
  sessionId: string;
  actionOrigin: ActionOrigin;
}

export default function SharedFormDefinitionPage(props: SharedFormDefinitionPageProps): JSX.Element {
  const [formDesignerState, setFormDesignerState] = useState<FormDesignerState>(
    props.initialFormDesignerState
  );

  const isFormCreation = props.id === undefined;

  // Keep track of the state that was last successfully saved in order to determine unsavedChangesExist.
  const [lastSavedState, setLastSavedState] = useState<FormDesignerState>(props.initialFormDesignerState);
  const unsavedChangesExist = useMemo(
    () =>
      // The form creation page always has unsaved changes, even if its equal to the initial state.
      isFormCreation ||
      // Designer states are not functionally equivalent.
      !areDesignerStatesEqual(lastSavedState, formDesignerState),
    [isFormCreation, lastSavedState, formDesignerState]
  );

  // Submittable if there is at least one section and action and every action has a schema.
  const isSubmittable = useMemo(() => {
    if (
      !props.name ||
      // Cannot create empty form definition
      !formDesignerState.sections ||
      // Cannot save if there are no changes to an existing form definition
      !(props.autofixUpdates || unsavedChangesExist)
    ) {
      return false;
    }
    const formActionConfigs = FormDesignerStateActions.generateFormActionConfigs(formDesignerState);
    return (
      Boolean(formActionConfigs?.length) && formActionConfigs.every((actionConfig) => actionConfig.schemaId)
    );
  }, [props.name, props.autofixUpdates, unsavedChangesExist, formDesignerState]);

  useWarnOnNavigate(
    unsavedChangesExist,
    `Are you sure you want to exit with unsaved changes to "${props.name}"?`
  );

  const onClickSave = async (): Promise<void> => {
    assert(formDesignerState);
    const formDefinition = FormDesignerStateActions.generateFormDefinition(formDesignerState);
    const formActionConfigs = FormDesignerStateActions.generateFormActionConfigs(formDesignerState);
    const saveSuccessful = await props.onSave(formDefinition, formActionConfigs, unsavedChangesExist);
    if (saveSuccessful) {
      setLastSavedState(formDesignerState);
    }
  };

  const [isBannerDismissed, setisBannerDismissed] = useState(false);

  const designer = (
    <>
      {props.autofixUpdates && !isBannerDismissed ? (
        <FormFixBanner
          taskSchemaName={props.autofixTaskSchemaName!}
          taskSchemaURL={props.autofixTaskSchemaURL!}
          updates={props.autofixUpdates}
          onDismiss={() => {
            setisBannerDismissed(true);
          }}
        />
      ) : null}
      <FormDesigner
        state={formDesignerState}
        setState={setFormDesignerState}
        lockActions={true}
        actionToSchemas={props.actionToSchemas}
        formDefinitionId={props.id ?? null}
        sessionId={props.sessionId}
        actionOrigin={props.actionOrigin}
      />
    </>
  );
  const preview = (
    <FormInstancePreview
      formDefinitionContent={FormDesignerStateActions.generateFormDefinition(formDesignerState)}
      formDesignerState={formDesignerState}
    />
  );

  return (
    <TabLayout
      header={
        <FormDefinitionPageHeader
          id={props.id}
          name={props.name}
          templateCollectionId={props.templateCollectionId}
          onUpdateTemplateCollectionId={props.setTemplateCollectionId}
          saveButtonText={props.saveButtonText}
          isSubmittable={isSubmittable}
          onUpdateName={props.setName}
          onClickSave={onClickSave}
          archivePurpose={props.archivePurpose}
          onArchive={props.onArchive}
          onUnarchive={props.onUnarchive}
        />
      }
      backgroundColor={TabLayoutBackgroundColor.WHITE}
    >
      <ResponsiveLayout designer={designer} preview={preview} />
    </TabLayout>
  );
}

const styles = StyleSheet.create({
  twoColumnContainer: {
    display: 'flex',
    minHeight: '100%',
  },
  designerColumnContainer: {
    paddingLeft: 30,
    minWidth: 0,
  },
  previewColumnContainer: {
    background: GRAY_LIGHTER,
    borderLeft: `1px solid ${GRAY_LIGHT}`,
    minWidth: 0,
  },
  designerTabContainer: {
    maxWidth: 600,
    paddingLeft: 6,
  },
  previewTabContainer: {
    maxWidth: 600,
    marginLeft: 'auto',
    marginRight: 'auto',
  },
});
